import { Box, Card, CardContent, Grid, Typography } from "@mui/material";

import SectionInputField from "./territoryOverview/SectionInputField";
import ActivePatientsQtdPanel from "./territoryOverview/panels/ActivePatientsQtdPanel";
import BreadthDepthPanel from "./territoryOverview/panels/BreadthDepthPanel";
import CompetitiveDynamicsPanel from "./territoryOverview/panels/CompetitiveDynamicsPanel";
import ReferralsQtdPanel from "./territoryOverview/panels/ReferralsQtdPanel";
import TopWritersPanel from "./territoryOverview/panels/TopWritersPanel";
import { AutoSyncedPill } from "./territoryOverview/primitives";
import { BODY, BORDER } from "./territoryOverview/tokens";

/**
 * Section 1 · Territory Overview
 *
 * @param {object} props
 * @param {import("./qbrTypes").TerritoryOverview} props.overview
 * @param {object} [props.onExplore]
 * @param {string|null} [props.quarterId] Compact id of the quarter shown in the dropdown (null/undefined = current quarter); forwarded to the two Section 1 free-text fields so edits made on a prior quarter land under that quarter.
 */
export default function TerritoryOverviewCard({
  overview,
  onExplore,
  quarterId,
}) {
  const attainmentProducts = overview.attainmentProducts ?? [];
  // `referralSplits` is the mapper's field name; `productSplits` kept as a defensive fallback for any older cached view model.
  const referralSplits =
    overview.referralSplits ?? overview.productSplits ?? [];
  // Active Patients QTD comes back pre-filtered to the rep's alignment (ROCS/CPS/ALD) or, for leadership, the full portfolio — the UI never special-cases a product line. An empty array renders the normal "No insights found" state.
  const activePatients = overview.activePatients ?? [];
  const activePatientSplits = overview.activePatientSplits ?? [];
  const breadthDepth = overview.breadthDepth ?? [];
  const topWriters = overview.topWriters ?? { columns: [], rows: [] };
  const competitive = overview.competitiveDynamics ?? {
    subtitle: "",
    claimants: [],
  };
  // Section 1 free-text fields seed from the API-side rep_inputs payload the mapper surfaces; empty strings fall out when no edit exists yet.
  const repInputs = overview.repInputs ?? {
    payor_reimbursement_dynamics: "",
    measures_of_success: "",
  };

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: "10px",
        border: `1px solid ${BORDER}`,
        boxShadow: "1px 4px 4px 0px #00000008",
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 2.5,
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 14, color: BODY }}>
            Territory Overview
          </Typography>
          <AutoSyncedPill lastRefreshTime={overview.lastRefreshTime} />
        </Box>

        <Grid container spacing={2.5}>
          <Grid item xs={12} lg={6}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <ReferralsQtdPanel
                attainmentProducts={attainmentProducts}
                referralSplits={referralSplits}
                onExplore={onExplore?.referralsQtd}
              />
              <ActivePatientsQtdPanel
                activePatients={activePatients}
                activePatientSplits={activePatientSplits}
                onExplore={onExplore?.activePatientsQtd}
              />
              <TopWritersPanel
                topWriters={topWriters}
                onExplore={onExplore?.topWriters}
              />
            </Box>
          </Grid>

          <Grid item xs={12} lg={6}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <BreadthDepthPanel
                breadthDepth={breadthDepth}
                onExplore={onExplore?.breadthDepth}
              />
              <CompetitiveDynamicsPanel
                competitive={competitive}
                onExplore={onExplore?.competitiveDynamics}
              />
            </Box>
          </Grid>
        </Grid>

        {/* Rep-authored notes — two-column row below Top Writers / Competitive Dynamics */}
        <Grid container spacing={2.5} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={6}>
            {/* Keyed by quarter so switching quarters remounts the field with that quarter's slice instead of reusing the prior quarter's local text (fixes cross-quarter bleed). */}
            <SectionInputField
              key={`payor_reimbursement_dynamics:${quarterId ?? "current"}`}
              sectionId="section1"
              fieldKey="payor_reimbursement_dynamics"
              label="Payor / Reimbursement Dynamics"
              placeholder="Note any payor or reimbursement dynamics worth flagging…"
              initialValue={repInputs.payor_reimbursement_dynamics}
              quarterId={quarterId}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <SectionInputField
              key={`measures_of_success:${quarterId ?? "current"}`}
              sectionId="section1"
              fieldKey="measures_of_success"
              label="Measures of Success This Quarter"
              placeholder="What will you monitor to know you're on track?"
              initialValue={repInputs.measures_of_success}
              quarterId={quarterId}
            />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}
